import React, {useCallback, useMemo, useState} from 'react';
import useTranslate from "../../../../modules/local/useTranslate";
import {BsBrushFill, BsCheck2, BsInfoCircle, BsPenFill, BsPersonFill} from "react-icons/bs";
import {PiPencilSimpleLight} from "react-icons/pi";
import {GoDotFill} from "react-icons/go";
import classNames from "classnames";
import _ from "lodash";

// Chip kết quả nhỏ, tông ngữ nghĩa (xanh lá = đúng, đỏ = sai, hổ phách = cần chấm).
const ResultChip = ({tone, label}) => {
    const map = {
        correct: 'bg-green-100 text-green-800',
        wrong: 'bg-red-100 text-red-700',
        pending: 'bg-amber-100 text-amber-800',
        graded: 'bg-green-100 text-green-800',
    };
    return (
        <span className={classNames('text-2xs font-semibold px-2.5 py-1 rounded-full whitespace-no-wrap', map[tone])}>
            {label}
        </span>
    );
};

/**
 * Câu trắc nghiệm / đúng-sai - máy đã chấm sẵn, ở đây chỉ hiển thị đối chiếu.
 * Đúng/sai lấy từ is_corrected (nhất quán với CourseClassExamQuestionIndexes).
 */
const AutoCorrectedQuestion = ({question, index}) => {
    const t = useTranslate();
    const isCorrect = question?.is_corrected === 1 || question?.is_corrected === true;
    const score = Number(question?.score) || 0;
    const maxScore = Number(question?.max_score) || 0;

    return (
        <div
            className={classNames(
                'background border border-color-50 rounded-xl overflow-hidden border-l-4',
                isCorrect ? 'border-l-green-400' : 'border-l-red-400'
            )}>
            {/* Header trung tính - tín hiệu đúng/sai nằm ở chip, không tô kín cả header */}
            <div className="px-4 py-3 border-b border-color-50 background-50 flex items-start justify-between gap-3">
                <h3 className="text-sm leading-snug text-color-000">
                    <span className="text-color-300 italic">{`${t('question')} ${index + 1} (${maxScore}đ): `}</span>
                    <span className="font-bold">{question.q}</span>
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-2xs text-color-400 font-medium whitespace-no-wrap">{score}/{maxScore}đ</span>
                    <ResultChip tone={isCorrect ? 'correct' : 'wrong'} label={isCorrect ? t('correct') : t('incorrect')}/>
                </div>
            </div>

            {/* Đáp án */}
            <div className="px-4 py-3 flex flex-col gap-2">
                {question.ops?.map((op, i) => {
                    const isStudentAnswer = op.is_answer === 1;
                    const isCorrectOption = op.is_correct === 1;

                    let optClass = 'background-50 text-color-300';
                    let icon = <span className="w-4 flex-shrink-0"/>;
                    let tag = null;

                    if (isStudentAnswer && isCorrectOption) {
                        optClass = 'bg-green-50 text-green-900 border border-green-300';
                        icon = <span className="w-4 h-4 flex flex-center flex-shrink-0 bg-green-600 rounded-full text-white text-2xs font-bold">✓</span>;
                        tag = <span className="text-2xs font-semibold text-green-700 flex-shrink-0">{t('chosen answer')}</span>;
                    } else if (isStudentAnswer && !isCorrectOption) {
                        optClass = 'bg-red-50 text-red-700 border border-red-300';
                        icon = <span className="w-4 h-4 flex flex-center flex-shrink-0 bg-red-500 rounded-full text-white text-2xs font-bold">✗</span>;
                        tag = <span className="text-2xs font-semibold text-red-600 flex-shrink-0">{t('chosen answer')}</span>;
                    } else if (!isStudentAnswer && isCorrectOption) {
                        optClass = 'bg-green-50 text-green-800 border border-green-300';
                        icon = <span className="w-4 h-4 flex flex-center flex-shrink-0 border-2 border-green-500 rounded-full text-green-600 text-2xs font-bold">✓</span>;
                        tag = <span className="text-2xs font-semibold text-green-700 flex-shrink-0">{t('correct answer')}</span>;
                    }

                    return (
                        <div key={i} className={classNames('flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm', optClass)}>
                            {icon}
                            <span className="flex-1">({t(op.value)}) {t(op.label)}</span>
                            {tag}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Câu tự luận - phần GV thực sự phải chấm tay.
 */
const EssayQuestionGrader = ({question, index, onScoreUpdate}) => {
    const t = useTranslate();
    const maxScore = Number(question?.max_score) || 0;

    const initial = (() => {
        const s = Number(question?.score);
        if (!isFinite(s)) return '0';
        return s % 1 === 0 ? String(s) : s.toFixed(1);
    })();
    const [tempScore, setTempScore] = useState(initial);

    const handleScoreChange = (e) => setTempScore(e.target.value);

    const handleBlur = () => {
        let num = parseFloat(tempScore);
        if (isNaN(num) || num < 0) num = 0;
        else if (num > maxScore) num = maxScore;
        setTempScore(num % 1 === 0 ? String(num) : num.toFixed(1));
        onScoreUpdate(question?.id, num);
    };

    const currentScore = parseFloat(tempScore) || 0;
    const isScored = question?.is_corrected === 1 || currentScore > 0;

    return (
        <div
            className={classNames(
                'background border border-color-50 rounded-xl overflow-hidden border-l-4',
                isScored ? 'border-l-green-400' : 'border-l-blue-400'
            )}>
            <div className="px-4 py-3 border-b border-color-50 bg-blue-50 flex items-start justify-between gap-3">
                <h3 className="text-sm leading-snug text-color-000">
                    <span className="text-color-300 italic">{`${t('question')} ${index + 1} (${maxScore}đ): `}</span>
                    <span className="font-bold">{question.q}</span>
                    <span className="ml-1 text-2xs text-color-300 italic">({t('essay')})</span>
                </h3>
                <ResultChip tone={isScored ? 'graded' : 'pending'} label={isScored ? t('graded') : t('needs grading')}/>
            </div>

            <div className="px-4 py-3">
                <p className="mb-1 text-2xs font-semibold text-color-400 uppercase tracking-wide">{t('student answer')}</p>
                <div className="px-3 py-2 background-50 rounded-lg text-sm text-color-000 whitespace-pre-wrap leading-relaxed">
                    {question?.a || <span className="italic text-color-300">{t('no answer')}</span>}
                </div>
            </div>

            <div className="px-4 py-2.5 border-t border-color-50 flex items-center justify-between gap-3">
                <label htmlFor={`score-${question?.id}`} className="flex items-center gap-1.5 text-xs text-color-300">
                    <PiPencilSimpleLight size={14} className="flex-shrink-0"/>
                    {_.capitalize(t('score'))}
                </label>
                <div className="flex items-center gap-2">
                    <input
                        id={`score-${question?.id}`} type="number"
                        value={tempScore} onChange={handleScoreChange} onBlur={handleBlur}
                        step="0.1" min="0" max={maxScore}
                        className="w-16 h-9 px-1 border border-color-100 rounded-lg text-sm font-bold text-center text-color-000 focus:border-blue-500"
                    />
                    <span className="text-xs font-medium text-color-300">/ {maxScore}đ</span>
                </div>
            </div>
        </div>
    );
};

const CourseClassExamAttemptEvaluateForm = ({attempt, course_class_exam, onSubmit, onCancel}) => {
    const t = useTranslate();
    const [attemptState, setAttemptState] = useState(attempt || {});

    const questions = attemptState.questions || [];
    const maxScore = Number(attemptState.max_score ?? course_class_exam?.max_score) || 0;

    const totalScore = useMemo(
        () => questions.reduce((s, q) => s + (Number(q.score) || 0), 0),
        [questions]
    );

    // Ngưỡng đạt: chỉ kết luận khi bài thi có khai passed_score. Chưa khai -> không tự cho "Chưa đạt".
    const hasThreshold = course_class_exam?.passed_score != null && course_class_exam?.passed_score !== '';
    const threshold = hasThreshold ? Number(course_class_exam.passed_score) : null;
    const isPass = hasThreshold ? totalScore >= threshold : null;

    const mcq = questions.filter((q) => q.type !== 'essay');
    const mcqCorrect = mcq.filter((q) => q.is_corrected === 1 || q.is_corrected === true).length;
    const essays = questions.filter((q) => q.type === 'essay');
    const essaysGraded = essays.filter((q) => q.is_corrected === 1 || (Number(q.score) || 0) > 0).length;

    // Bài toàn trắc nghiệm ĐÃ chấm xong (status=9) -> không còn gì để sửa (trắc nghiệm chỉ đọc,
    // không có ô nhập nào) - "Lưu kết quả chấm bài" lúc này chỉ gửi lại đúng điểm cũ, vô nghĩa và
    // dễ hiểu lầm là còn thao tác gì đó. Đổi màn thành CHỈ XEM, nút Lưu -> Đóng.
    // - Còn câu tự luận: luôn cần nút Lưu (GV có thể chấm/chấm lại điểm tự luận bất cứ lúc nào).
    // - Chưa chấm lần nào (status !== 9): vẫn cần bấm Lưu để CHỐT (set is_passed + status=9),
    //   kể cả khi toàn trắc nghiệm.
    const isFinalized = Number(attemptState.status) === 9
    const readOnly = isFinalized && essays.length === 0

    const handleScoreUpdate = useCallback((questionId, newScore) => {
        setAttemptState((prev) => ({
            ...prev,
            questions: (prev.questions || []).map((q) =>
                q.id === questionId ? {...q, score: newScore, is_corrected: 1} : q
            ),
        }));
    }, []);

    const handleSaveGrade = () => {
        onSubmit({
            questions_json: JSON.stringify(
                questions.map((q) => ({
                    id: q.id,
                    score: q.score,
                    is_corrected: q.is_corrected || 0,
                }))
            ),
            is_passed: isPass == null ? null : (isPass ? 1 : 0),
            status: 9,
        });
    };

    return (
        <div className="mb-28">
            <div className="max-w-3xl mx-auto flex flex-col gap-5">

                {/* Thông tin chung */}
                <header className="p-4 background border border-color-50 shadow-sm rounded-2xl">
                    <h1 className="mb-3 flex items-center gap-2 font-bold text-lg tracking-wide">
                        <BsPenFill size={15} className="text-blue-500"/>
                        {course_class_exam?.title}
                    </h1>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-3 background-50 rounded-xl">
                            <div className="mb-1 flex items-center gap-1.5 font-semibold text-2xs text-color-400 uppercase tracking-wider">
                                <BsPersonFill size={13}/> {t('students')}
                            </div>
                            <p className="font-semibold text-sm text-color-000">
                                {attemptState.student?.fullname}
                                <span className="ml-1 font-normal text-xs text-blue-500">({attemptState.student?.student_idcode})</span>
                            </p>
                            {attemptState.student?.patron_saint_name && (
                                <p className="flex items-center gap-1 text-xs text-color-300">
                                    <GoDotFill size={9} className="text-color-500"/> {t('holy name')}: {attemptState.student.patron_saint_name}
                                </p>
                            )}
                        </div>
                        <div className="p-3 background-50 rounded-xl">
                            <div className="mb-1 flex items-center gap-1.5 font-semibold text-2xs text-color-400 uppercase tracking-wider">
                                <BsBrushFill size={12}/> {t('exam')}
                            </div>
                            <p className="font-semibold text-sm text-color-000">{course_class_exam?.title}</p>
                            <p className="flex items-center gap-1 text-xs text-color-300">
                                <GoDotFill size={9} className="text-color-500"/> {t('time')}: {course_class_exam?.duration_minutes} {t('minutes')}
                            </p>
                        </div>
                    </div>
                </header>

                {/* Tóm tắt */}
                <div className="px-4 py-3 background border border-color-50 rounded-xl flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs font-semibold">
                    <span className="text-color-300">
                        {t('multiple answers')}: <span className="text-green-700">{mcqCorrect}/{mcq.length} {t('correct')}</span>
                    </span>
                    {essays.length > 0 && (
                        <span className="text-color-300">
                            {t('essay')}: <span className={essaysGraded < essays.length ? 'text-amber-700' : 'text-green-700'}>{essaysGraded}/{essays.length} {t('graded')}</span>
                        </span>
                    )}
                    <span className="ml-auto text-color-300">
                        {t('current total score')}: <span className="text-blue-700 text-sm">{totalScore % 1 === 0 ? totalScore : totalScore.toFixed(2)}</span> / {maxScore}
                    </span>
                </div>

                {/* Ghi chú CHỈ XEM - giải thích vì sao không còn nút Lưu/sửa, tránh GV thắc mắc
                    tưởng màn bị lỗi/thiếu quyền. */}
                {readOnly && (
                    <div className="px-4 py-3 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl">
                        <BsInfoCircle size={15} className="text-blue-500 flex-shrink-0 mt-0.5"/>
                        <span className="text-xs text-blue-700 leading-relaxed">
                            {t('exam evaluate readonly note')}
                        </span>
                    </div>
                )}

                {/* Danh sách câu hỏi */}
                <section className="flex flex-col gap-3">
                    <h2 className="font-bold text-base text-color-000 tracking-wide">
                        {t('exam details')} ({questions.length} {t('sentence')})
                    </h2>
                    {questions.map((q, index) =>
                        q.type === 'essay' ? (
                            <EssayQuestionGrader key={q.id} question={q} index={index} onScoreUpdate={handleScoreUpdate}/>
                        ) : (
                            <AutoCorrectedQuestion key={q.id} question={q} index={index}/>
                        )
                    )}
                </section>
            </div>

            {/* Footer cố định - 1 hàng: tổng điểm · kết luận đạt/chưa đạt (chỉ hiển thị) · lưu */}
            <footer className="fixed bottom-0 left-0 right-0 background border-t border-color-100 shadow-2xl p-3 z-10">
                <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex flex-col leading-tight">
                        <span className="text-2xs text-color-400 font-medium uppercase tracking-wide">{t('current total score')}</span>
                        <span className="text-2xl font-extrabold text-blue-600">
                            {totalScore % 1 === 0 ? totalScore : totalScore.toFixed(2)}
                            <span className="text-sm font-medium text-color-400"> / {maxScore}</span>
                        </span>
                    </div>

                    <div className="flex flex-col items-center leading-tight">
                        {isPass == null ? (
                            <span className="text-xs italic text-color-300">{t('pass mark')}: —</span>
                        ) : (
                            <React.Fragment>
                                <span className={classNames(
                                    'px-3 py-1 rounded-full text-sm font-bold',
                                    isPass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                )}>
                                    {isPass ? t('passed') : t('failed')}
                                </span>
                                <span className="mt-0.5 text-2xs text-color-400">{t('pass mark')}: {threshold}đ</span>
                            </React.Fragment>
                        )}
                    </div>

                    {readOnly ? (
                        <button
                            onClick={onCancel}
                            className="px-6 py-3 flex flex-center gap-2 uppercase background-100 hover:background-200 rounded-xl font-bold text-sm text-color-100 transition-colors">
                            {t('close')}
                        </button>
                    ) : (
                        <button
                            onClick={handleSaveGrade}
                            className="px-6 py-3 flex flex-center gap-2 uppercase bg-green-600 hover:bg-green-700 rounded-xl font-bold text-sm text-white shadow-md transition-colors">
                            <BsCheck2 size={17}/>
                            {t('save grading result')}
                        </button>
                    )}
                </div>
            </footer>
        </div>
    );
};

export default CourseClassExamAttemptEvaluateForm;
